import { createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// Transform to handle Date objects
const dateTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: any) => {
    const processValue = (value: any): any => {
      if (value instanceof Date) {
        return {
          __type: 'Date',
          value: value.toISOString()
        };
      }
      if (Array.isArray(value)) {
        return value.map(processValue);
      }
      if (value !== null && typeof value === 'object') {
        const processed: any = {};
        for (const key in value) {
          processed[key] = processValue(value[key]);
        }
        return processed;
      }
      return value;
    };
    return processValue(inboundState);
  },
  // Transform state being rehydrated
  (outboundState: any) => {
    const processValue = (value: any): any => {
      if (value && typeof value === 'object' && value.__type === 'Date') {
        return new Date(value.value);
      }
      if (Array.isArray(value)) {
        return value.map(processValue);
      }
      if (value !== null && typeof value === 'object' && !value.__type) {
        const processed: any = {};
        for (const key in value) {
          processed[key] = processValue(value[key]);
        }
        return processed;
      }
      return value;
    };
    return processValue(outboundState);
  }
);

// Transform to handle large photo data (store references only)
const photoTransform = createTransform(
  // Transform state on its way to being serialized
  (inboundState: any) => {
    if (!inboundState.photoMap) return inboundState;
    
    // Store only essential photo metadata, not base64 data
    const lightPhotoMap: any = {};
    for (const key in inboundState.photoMap) {
      const photo = inboundState.photoMap[key];
      if (photo) {
        lightPhotoMap[key] = {
          type: photo.type,
          uploadStatus: photo.uploadStatus,
          filename: photo.filename,
          // Store a reference ID instead of actual URI/base64
          photoId: `photo_${key}_${Date.now()}`
        };
      }
    }
    
    return {
      ...inboundState,
      photoMap: lightPhotoMap
    };
  },
  // Transform state being rehydrated
  (outboundState: any) => {
    // Photo data will be restored from IndexedDB using photoId references
    return outboundState;
  },
  // Apply only to vehiclePhotos reducer
  { whitelist: ['vehiclePhotos'] }
);

// Main persistence configuration
export const persistConfig = {
  key: 'shzoom-uptime-root',
  storage,
  version: 1,
  whitelist: [
    'auth',           // Persist authentication state
    'vehicle',        // Persist vehicle information
    'vehiclePhotos',  // Persist photo metadata (not actual images)
    'damageLocations', // Persist damage locations
    'damagedParts',   // Persist damaged parts
    'incidentDetails', // Persist incident details
    'operatorInfo',   // Persist operator information
    'history',        // Persist incident history
    'navigation'      // Persist navigation state
  ],
  transforms: [dateTransform, photoTransform],
  migrate: (state: any) => {
    // Handle future migrations between versions
    return Promise.resolve(state);
  },
  // Debounce writes to improve performance
  throttle: 1000,
  // Merge strategy for rehydration
  stateReconciler: (inboundState: any, originalState: any) => {
    // Deep merge strategy preserving new state structure
    const reconciled = { ...originalState };
    
    for (const key in inboundState) {
      if (inboundState[key] !== undefined) {
        reconciled[key] = inboundState[key];
      }
    }
    
    return reconciled;
  }
};

// Configuration for session-only data (uses sessionStorage)
export const sessionPersistConfig = {
  key: 'shzoom-uptime-session',
  storage: {
    getItem: (key: string) => sessionStorage.getItem(key),
    setItem: (key: string, value: string) => sessionStorage.setItem(key, value),
    removeItem: (key: string) => sessionStorage.removeItem(key)
  },
  whitelist: ['currentStep', 'navigationState'],
  throttle: 500
};

// Storage size monitoring
export const getStorageSize = (): { used: number; quota: number } => {
  let used = 0;
  let quota = 5 * 1024 * 1024; // Default 5MB quota
  
  try {
    // Calculate localStorage usage
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }
    
    // Try to get actual quota if available
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        if (estimate.quota) {
          quota = estimate.quota;
        }
      });
    }
  } catch (error) {
    console.error('Error calculating storage size:', error);
  }
  
  return { used, quota };
};

// Auto cleanup old data when storage is nearly full
export const autoCleanupStorage = async (threshold = 0.9): Promise<boolean> => {
  const { used, quota } = getStorageSize();
  const usage = used / quota;
  
  if (usage > threshold) {
    try {
      // Clear old incident data first
      const keys = Object.keys(localStorage);
      const incidentKeys = keys.filter(key => key.includes('incident_history_'));
      
      // Sort by timestamp and remove oldest
      incidentKeys.sort().slice(0, Math.floor(incidentKeys.length / 2)).forEach(key => {
        localStorage.removeItem(key);
      });
      
      return true;
    } catch (error) {
      console.error('Error during auto cleanup:', error);
      return false;
    }
  }
  
  return false;
};

// Persistence gate configuration
export const persistGateConfig = {
  loading: null, // Will be replaced with a loading component
  persistor: null as any // Will be set by the store
};